import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import { useAnalytics } from '@common/analytics-layer/composables/useAnalytics'
import { AnalyticsEvents } from '@common/analytics-layer/types'
import { useLearnerApiQueryStore } from '@courses/learner-api-layer/stores/learner-api.query-store'
import { type CompletedLessons } from '@courses/types-layer/types/learner'
import { useCourseNotification } from '@courses/utility-layer/helper'

import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

import { PostCompletionLearnerService } from '../services/post-completion.learner.service'

export interface PostCompletionLearnerQueryStoreOptions {
  /** When false, completed lessons are not fetched (catalog / bundle preview). Default true. */
  fetchCompletedLessons?: MaybeRefOrGetter<boolean>
}

export const usePostCompletionLearnerQueryStore = (
  courseId: MaybeRefOrGetter<string>,
  locationId: MaybeRefOrGetter<string>,
  options?: PostCompletionLearnerQueryStoreOptions
) => {
  const queryClient = useQueryClient()
  const service = new PostCompletionLearnerService()
  const { open: openNotification } = useCourseNotification()
  const { queryKeys: learnerApiKeys } = useLearnerApiQueryStore(courseId, locationId)

  const fetchCompletedLessonsEnabled = computed(() => {
    const o = options?.fetchCompletedLessons
    return o !== undefined ? toValue(o) : true
  })

  const queryKeys = {
    all: (cId?: string, locId?: string) => ['post-completion-learner', cId, locId] as const,
    completedLessons: (cId?: string, locId?: string) => ['post-completion-learner', cId, locId, 'completed-lessons'] as const,
  }

  const fetchCompletedLessonsQuery = () => {
    return useQuery<CompletedLessons, Error>({
      queryKey: computed(() => queryKeys.completedLessons(toValue(courseId), toValue(locationId))),
      queryFn: async (): Promise<CompletedLessons> =>
        await service.getCompletedLessons({
          courseId: toValue(courseId),
          courseLocationId: toValue(locationId),
        }),
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => fetchCompletedLessonsEnabled.value && !!toValue(courseId) && !!toValue(locationId)),
    })
  }

  const markPostAsCompletedMutation = useMutation({
    mutationFn: async (payload: {
      productId: string
      postId: string
      moduleId: string
      percentage?: number
      userId?: string
      contactId?: string
    }) =>
      await service.markPostAsCompleted({
        courseId: toValue(courseId),
        moduleId: payload.moduleId,
        lessonId: payload.postId,
        locationId: toValue(locationId),
        payload,
      }),
    onMutate: newData => {
      const queryKey = queryKeys.completedLessons(toValue(courseId), toValue(locationId))
      const previousData: Record<string, any> = queryClient.getQueryData(queryKey) || {}

      const existingLessons: any[] = previousData.completedLessons || []
      const existingIndex = existingLessons.findIndex((lesson: any) => lesson.postId === newData.postId)

      const newEntry = {
        id: newData.postId,
        percentage: newData.percentage,
        postId: newData.postId,
      }

      const updatedCompletedLessons =
        existingIndex >= 0
          ? existingLessons.map((lesson: any, i: number) => (i === existingIndex ? { ...lesson, ...newEntry } : lesson))
          : [...existingLessons, newEntry]

      const updatedData = {
        ...previousData,
        completedLessons: updatedCompletedLessons,
      }

      queryClient.setQueryData(queryKey, () => updatedData)
      return {
        previousData,
      }
    },
    onError: (error, variables, context) => {
      const queryKey = queryKeys.completedLessons(toValue(courseId), toValue(locationId))
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      openNotification('Error', 'Failed to mark post as completed')
    },
    onSuccess: (_data, variables) => {
      try {
        const analytics = useAnalytics()
        analytics.track(AnalyticsEvents.LESSON_COMPLETED, {
          courseId: toValue(courseId),
          lessonId: variables.postId,
          moduleId: variables.moduleId,
          locationId: toValue(locationId),
          percentage: variables.percentage ?? 100,
        })
      } catch (error) {
        console.warn('Analytics tracking failed:', error)
      }

      // NOTE: We don't invalidate completedLessons to avoid backend caching bugs
      // but we MUST invalidate unlocked modules so the next lesson unlocks
      queryClient.invalidateQueries({
        queryKey: learnerApiKeys.unlockedModules(toValue(locationId), toValue(courseId)),
      })
    },
  })

  const markPostAsIncompleteMutation = useMutation({
    mutationFn: async (params: { completionRecordId: string; postId: string; productId: string; moduleId: string }) =>
      await service.markPostAsIncomplete({
        courseId: toValue(courseId),
        moduleId: params.moduleId,
        lessonId: params.postId,
        locationId: toValue(locationId),
        completionRecordId: params.completionRecordId,
      }),
    onMutate: newData => {
      const queryKey = queryKeys.completedLessons(toValue(courseId), toValue(locationId))
      const previousData: Record<string, any> = queryClient.getQueryData(queryKey) || {}

      const updatedData = {
        ...previousData,
        completedLessons: previousData.completedLessons.filter((lesson: any) => lesson.postId !== newData.postId),
      }

      queryClient.setQueryData(queryKey, () => updatedData)
      return {
        previousData,
      }
    },
    onError: (error, variables, context) => {
      const queryKey = queryKeys.completedLessons(toValue(courseId), toValue(locationId))
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      openNotification('Error', 'Failed to mark post as incomplete')
    },
    onSuccess: () => {
      // NOTE: We don't invalidate completedLessons to avoid backend caching bugs
      // but we MUST invalidate unlocked modules so subsequent lessons re-lock if needed
      queryClient.invalidateQueries({
        queryKey: learnerApiKeys.unlockedModules(toValue(locationId), toValue(courseId)),
      })
    },
  })

  return {
    fetchCompletedLessonsQuery,
    markPostAsCompletedMutation,
    markPostAsIncompleteMutation,
  }
}
